import { AttributeHelper } from "./Helpers";
import * as THREE from "three";
import { PlanogramRenderer } from "./PlanogramRenderer";
import { PlanogramContainer } from "./PlanogramContainer";
import { PlanogramPosition } from "./PlanogramPosition";
import { PlanogramComponent } from "./PlanogramComponent";
import { PlanogramRendererResources } from "./Resources";
import { PlanogramObject } from "./PlanogramObject";



/**
 * @typedef PlanogramLabelData
 * @property {ImageData} imageData
 * @property {number} innerWidth
 * @property {number} innerHeight
 * @property {string} textDirection
*/

/**
 * @typedef PlanogramLabelImageData
 * @property {string} id
 * @property {string} text
 * @property {PlanogramLabelData} x
 * @property {PlanogramLabelData} y
 * @property {PlanogramLabelData} z
 */

/**
 * Controller for Planogram Labels, sets the attributes and properties for all labels.
 * @extends HTMLElement
 */
export class PlanogramLabel extends HTMLElement{
    static attributes = {
        //numbers
        BorderThickness: "border-thickness",
        BorderColour: "border-colour",
        BackgroundColour: "background-colour",
        FontSize: "font-size",
        MinimumFontSize: "minimum-font-size",
        FontColour: "font-colour",
        Width: "width",
        Height: "height",
        Depth: "depth",
        Margin: "margin",

        //strings
        FontFamily: "font-family",

        //enums
        Type: "type",
        TextWrapping: "text-wrapping",
        TextDirection: "text-direction",
        TextAlignment: "text-alignment",
        VerticalAlignment: "vertical-alignment",
        HorizontalAlignment: "horizontal-alignment",

        //booleans
        Bold: "bold",
        Italic: "italic",
        Underline: "underline",
        ShrinkToFit: "shrink-to-fit",
        RotateToFit: "rotate-to-fit",
        OverImages: "over-images",
    }

    static observedAttributes = [
        ...Object.values(PlanogramLabel.attributes)
    ];

    //#region Attribute Properties
    get showMargin(){ return this.hasAttribute(PlanogramLabel.helperAttributes.ShowMargin); }
    get borderThickness(){ return this.#helper.numberFromAttribute(PlanogramLabel.attributes.BorderThickness, 0.25); }
    get borderColour(){ return this.#helper.numberFromAttribute(PlanogramLabel.attributes.BorderColour, 0); }
    get backgroundColour(){ return this.#helper.numberFromAttribute(PlanogramLabel.attributes.BackgroundColour, 0xffffff); }
    get fontSize(){ return this.#helper.numberFromAttribute(PlanogramLabel.attributes.FontSize, 20); }
    get minimumFontSize(){ return this.#helper.numberFromAttribute(PlanogramLabel.attributes.MinimumFontSize, 2); }
    get fontColour(){ return this.#helper.numberFromAttribute(PlanogramLabel.attributes.FontColour, 0); }
    get width(){ return this.#helper.numberFromAttribute(PlanogramLabel.attributes.Width, 1); }
    get height(){ return this.#helper.numberFromAttribute(PlanogramLabel.attributes.Height, 1); }
    get depth(){ return this.#helper.numberFromAttribute(PlanogramLabel.attributes.Depth, 1); }
    get fontFamily(){ return this.getAttribute(PlanogramLabel.attributes.FontFamily) || "Arial"; }
    get type(){ return this.#helper.enumFromAttribute(PlanogramLabel.attributes.Type, PlanogramLabel.type, PlanogramLabel.type.Product); }
    get textWrapping(){ return this.#helper.enumFromAttribute(PlanogramLabel.attributes.TextWrapping, PlanogramLabel.textWrapping, PlanogramLabel.textWrapping.Word); }
    get textDirection(){ return this.#helper.enumFromAttribute(PlanogramLabel.attributes.TextDirection, PlanogramLabel.textDirection, PlanogramLabel.textDirection.Horizontal); }
    get textAlignment(){ return this.#helper.enumFromAttribute(PlanogramLabel.attributes.TextAlignment, PlanogramLabel.textAlignment, PlanogramLabel.textAlignment.Left); }
    get verticalAlignment(){ return this.#helper.enumFromAttribute(PlanogramLabel.attributes.VerticalAlignment, PlanogramLabel.verticalPlacement, PlanogramLabel.verticalPlacement.Center); }
    get horizontalAlignment(){ return this.#helper.enumFromAttribute(PlanogramLabel.attributes.HorizontalAlignment, PlanogramLabel.horizontalPlacement, PlanogramLabel.horizontalPlacement.Center); }
    get bold(){ return this.hasAttribute(PlanogramLabel.attributes.Bold); }
    get italic(){ return this.hasAttribute(PlanogramLabel.attributes.Italic); }
    get underline(){ return this.hasAttribute(PlanogramLabel.attributes.Underline); }
    get shrinkToFit(){ return this.hasAttribute(PlanogramLabel.attributes.ShrinkToFit); }
    get rotateToFit(){ return this.hasAttribute(PlanogramLabel.attributes.RotateToFit); }
    get overImages(){ return this.hasAttribute(PlanogramLabel.attributes.OverImages); }
    
    //#endregion


    //#region Mesh Properties
    /**@type {THREE.InstancedMesh} */
    #orthographicMesh = null;
    /**@type {THREE.InstancedMesh} */
    #perspectiveMesh = null;
    /**@type {THREE.BoxGeometry} */
    #orthographicGeometry = null;
    /**@type {THREE.PlaneGeometry} */
    #perspectiveGeometry = null;
    /**@type {PlanogramLabelController} */
    #labelController = null;
    /**@type {PlanogramContainer} */
    #container = null;
    get #name(){ return this.type === PlanogramLabel.type.Product ? PlanogramPosition.name : PlanogramComponent.name; }
    /**@type {THREE.Mesh[]} */
    get #meshes(){ return this.#container?.scene.getObjectsByProperty("name", this.#name) || [] }
    /**@type {number} */
    get #instanceCount() { return this.#meshes?.length || 0; }
    //#endregion

    #helper = new AttributeHelper(this,(96/72));
    #firstRender = true;

    constructor(){ super(); }

    generateMeshes(){
        if( !this.#container || !this.isConnected) return;
        if(this.#instanceCount === 0){
            if(this.#orthographicMesh){
                this.#orthographicMesh.removeFromParent();
                this.#orthographicMesh.dispose();
                this.#orthographicMesh = null;
            }
            if(this.#perspectiveMesh){
                this.#perspectiveMesh.removeFromParent();
                this.#perspectiveMesh.dispose();
                this.#perspectiveMesh = null;
            }
            return;
        }
        
        if(!this.#orthographicGeometry)
            this.#orthographicGeometry = new THREE.BoxGeometry(1,1,1);
        if(!this.#perspectiveGeometry)
            this.#perspectiveGeometry = new THREE.PlaneGeometry(1,1);

        if(!this.#orthographicMesh || this.#orthographicMesh.count !== this.#instanceCount){
            if(this.#orthographicMesh){
                this.#orthographicMesh.removeFromParent();
                this.#orthographicMesh.dispose();
            }
            this.#orthographicMesh = new THREE.InstancedMesh( this.#orthographicGeometry, this.#container.textShader, this.#instanceCount );
            this.#orthographicMesh.name = this.type + "LabelOrthographic";
            this.#orthographicMesh.layers.set(PlanogramRendererResources.ORTHOGRAPHIC_LAYER);
            this.#container.addToScene(this.#orthographicMesh);
        }
        if(!this.#perspectiveMesh || this.#perspectiveMesh.count !== this.#instanceCount){
            if(this.#perspectiveMesh){
                this.#perspectiveMesh.removeFromParent();
                this.#perspectiveMesh.dispose();
            }
            this.#perspectiveMesh = new THREE.InstancedMesh( this.#perspectiveGeometry, this.#container.textShader, this.#instanceCount );
            this.#perspectiveMesh.name = this.type + "LabelPerspective";
            this.#perspectiveMesh.layers.set(PlanogramRendererResources.PERSPECTIVE_LAYER);
            this.#container.addToScene(this.#perspectiveMesh);
        }
        
        // Set render order based on showPositionsThroughBlocking (match positions behavior)
        const showAboveBlocking = this.#container?.showPositionsThroughBlocking;
        if(this.#orthographicMesh) this.#orthographicMesh.renderOrder = showAboveBlocking ? 21 : 1;
        if(this.#perspectiveMesh) this.#perspectiveMesh.renderOrder = showAboveBlocking ? 21 : 1;

        if(this.#orthographicMesh){
            const matrix = new THREE.Matrix4();
            for(let i=0; i<this.#instanceCount; i++){
                const mesh = this.#meshes[i];
                const size = mesh.userData.boundingBox.getSize(new THREE.Vector3()).add(new THREE.Vector3(0.1, 0.1, 0.1));
                const centre = mesh.userData.boundingBox.getCenter(new THREE.Vector3());
                matrix.identity().compose(centre, new THREE.Quaternion(), size);
                this.#orthographicMesh.setMatrixAt(i, matrix);
            }
            this.#orthographicMesh.instanceMatrix.needsUpdate = true;
        }

        if(this.#perspectiveMesh){
            const matrix = new THREE.Matrix4();
            for(let i=0; i<this.#instanceCount; i++){
                const mesh = this.#meshes[i];
                const size = mesh.userData.boundingBox.getSize(new THREE.Vector3()).add(new THREE.Vector3(0.1, 0.1, 0.1));
                const centre = mesh.userData.boundingBox.getCenter(new THREE.Vector3());
                const frontCenter = centre.clone().add(new THREE.Vector3(0, 0, size.z / 2));
                matrix.identity().compose(frontCenter, new THREE.Quaternion(), new THREE.Vector3(size.x, size.y, 1));
                this.#perspectiveMesh.setMatrixAt(i, matrix);
            }
            this.#perspectiveMesh.instanceMatrix.needsUpdate = true;
        }

        const hAlignment = this.horizontalAlignment === PlanogramLabel.horizontalPlacement.Left ? -1 :
                    this.horizontalAlignment === PlanogramLabel.horizontalPlacement.Center ? 0 : 1;
        
        const vAlignment = this.verticalAlignment === PlanogramLabel.verticalPlacement.Top ? -1 :
            this.verticalAlignment === PlanogramLabel.verticalPlacement.Center ? 0 : 1;

        // x: width, y: height, z: depth
        const size = [];
        // x: innerWidth, y: innerHeight, z:imageDepth, w: text-direction
        const frontBack = [];
        // x: innerWidth, y: innerHeight, z:imageDepth, w: text-direction
        const leftRight = [];
        // x: innerWidth, y: innerHeight, z:imageDepth, w: text-direction
        const topBottom = [];
        // x: h-alignment, y: v-alignment, z: isAttached
        const labelOptions = []
        // x: border colour, y: background colour, z: text colour, w: border 
        const colourOptions = [];

        let hasValidData = false;
        
        this.#meshes.forEach( mesh => {
            const data = this.#container.labelController.getImageDepth( mesh.userData.id );
            const imageData = this.#container.labelController.getImageData( mesh.userData.id );
            if(!data || !imageData) return;
            
            hasValidData = true;
            const boundingBox = mesh.userData.boundingBox;

            const [ xDepth, yDepth, zDepth ] = data;
            size.push(...boundingBox.getSize(new THREE.Vector3()).add(new THREE.Vector3(0.1, 0.1, 0.1)).toArray());
            frontBack.push(imageData.x.innerWidth, imageData.x.innerHeight, xDepth, imageData.x.textDirection === PlanogramLabel.textDirection.Horizontal ? 0 : 1);
            leftRight.push(imageData.y.innerWidth, imageData.y.innerHeight, yDepth, imageData.y.textDirection === PlanogramLabel.textDirection.Horizontal ? 0 : 1);
            topBottom.push(imageData.z.innerWidth, imageData.z.innerHeight, zDepth, imageData.z.textDirection === PlanogramLabel.textDirection.Horizontal ? 0 : 1);
            labelOptions.push(hAlignment, vAlignment, 1);
            colourOptions.push(this.borderColour, this.backgroundColour, this.fontColour, this.borderThickness);
        });

        // Hide meshes if no valid data yet to prevent black labels
        if (this.#orthographicMesh) this.#orthographicMesh.visible = hasValidData;
        if (this.#perspectiveMesh) this.#perspectiveMesh.visible = hasValidData;
        
        if (!hasValidData) return;

        const applyAttributes = geometry => {
            geometry.setAttribute("size", new THREE.InstancedBufferAttribute( new Float32Array(size), 3 ));
            geometry.setAttribute("frontBack", new THREE.InstancedBufferAttribute( new Float32Array(frontBack), 4 ));
            geometry.setAttribute("leftRight", new THREE.InstancedBufferAttribute( new Float32Array(leftRight), 4 ));
            geometry.setAttribute("topBottom", new THREE.InstancedBufferAttribute( new Float32Array(topBottom), 4 ));
            geometry.setAttribute("labelOptions", new THREE.InstancedBufferAttribute( new Float32Array(labelOptions), 3 ));
            geometry.setAttribute("colourOptions", new THREE.InstancedBufferAttribute( new Float32Array(colourOptions), 4 ));
        };

        if(this.#orthographicGeometry) applyAttributes(this.#orthographicGeometry);
        if(this.#perspectiveGeometry) applyAttributes(this.#perspectiveGeometry);

    }

    /**
     * @param {PlanogramObject} planObject 
     */
    updateFromPlanObject( planObject ){
        if(!planObject || !planObject.groupBoundingBox) return;
        setTimeout(async () => await this.updateFromAllMeshes(), 0);
    }
    
    /**
     * Update render order when showPositionsThroughBlocking changes
     * @param {boolean} showAbove 
     */
    applyShowPositionsThroughBlocking(showAbove){
        if(this.#orthographicMesh) this.#orthographicMesh.renderOrder = showAbove ? 21 : 1;
        if(this.#perspectiveMesh) this.#perspectiveMesh.renderOrder = showAbove ? 21 : 1;
    }

    async updateFromAllMeshes(){
        const meshesToUpdate = this.#meshes.
            filter(x=>Reflect.has(x.userData, "label") && x.userData.label).
            filter(x=>{
                const data = this.#labelController.getImageData( x.userData.id );
                return !data || data.text !== x.userData.label;
            });
        
        if (meshesToUpdate.length === 0) {
            this.generateMeshes();
            return;
        }
        
        // Process all labels directly without workers
        const results = await Promise.all(
            meshesToUpdate.map(mesh => {
                const size = mesh.userData.boundingBox.getSize(new THREE.Vector3());
                return this.#applyFontTransforms(mesh.userData.label, size, mesh.userData.id);
            })
        );
        
        await this.#labelController.updateBulk([Promise.resolve(results)]);
        this.generateMeshes();
    }

    // /** @type {HTMLCanvasElement} */
    // #canvas;
    // /** @type {CanvasRenderingContext2D} */
    // #canvasContext;

    // #setCanvas(){
    //     const scaleFactor = 4;
    //     const canvas = document.createElement('canvas');
    //     canvas.width = PlanogramLabelController.maxRestriction.width;
    //     canvas.height = PlanogramLabelController.maxRestriction.width;
    //     const context = canvas.getContext('2d',{ willReadFrequently: true });
    //     context.scale(scaleFactor, scaleFactor);
    //     context.fillStyle = `#${this.backgroundColour.toString(16)}`;
    //     context.textBaseline = "top";
    //     this.#canvas = canvas;
    //     this.#canvasContext = context;
    // }

    // Reuse canvas across label generations for better performance
    /**@type {HTMLCanvasElement} */
    static #sharedCanvas = null;
    /**@type {CanvasRenderingContext2D} */
    static #sharedContext = null;
    
    /**
     * @param {string} labelText
     * @param {THREE.Vector3} size
     * @param {string} id
     * @returns {Promise<PlanogramLabelImageData>}
     */
    #applyFontTransforms( labelText, size, id ){
        const scaleFactor = 4;
        const width = PlanogramLabelController.maxRestriction.width;
        
        // Reuse shared canvas
        if (!PlanogramLabel.#sharedCanvas) {
            PlanogramLabel.#sharedCanvas = document.createElement('canvas');
            PlanogramLabel.#sharedCanvas.width = width * 3;
            PlanogramLabel.#sharedCanvas.height = width;
            PlanogramLabel.#sharedContext = PlanogramLabel.#sharedCanvas.getContext('2d', { willReadFrequently: true });
            PlanogramLabel.#sharedContext.scale(scaleFactor, scaleFactor);
        }
        
        const canvas = PlanogramLabel.#sharedCanvas;
        const context = PlanogramLabel.#sharedContext;
        
        // Clear canvas for new render
        context.save();
        context.setTransform(1, 0, 0, 1, 0, 0);
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.restore();
        
        context.fillStyle = `#${this.backgroundColour.toString(16).padStart(6, '0')}`;
        context.textBaseline = "top";

        const horizontal = PlanogramLabel.textDirection.Horizontal;
        const vertical = PlanogramLabel.textDirection.Vertical;
        const text = this.textWrapping === PlanogramLabel.textWrapping.Letter ? 
            labelText.split(""): this.textWrapping === PlanogramLabel.textWrapping.Word ?
            labelText.split(" ") : [labelText]
        const margin = 0.1 + this.borderThickness;

        const getDimensions = (textDirection, maxWidth, minHeight) => {
            return {
                width: textDirection === horizontal ? maxWidth : minHeight,
                height: textDirection === horizontal ? minHeight : maxWidth
            }
        }

        const getInnerDimensions = ( width, height ) => {
            const scale = 2;
            return {
                innerMaxWidth: Math.max((width * scale) - (margin * 2), 0.1),
                innerMaxHeight: Math.max((height * scale) - (margin * 2), 0.1),
            }
        }

        const roundToDecimalPlaces = (num, decimalPlaces) => {
            const factor = Math.pow(10, decimalPlaces);
            return Math.round(num * factor) / factor;
        }

        // Cache text measurements to avoid redundant calculations
        const measurementCache = new Map();
        const getLongestLine = text => {
            let maxWidth = 0;
            for (const item of text) {
                let w = measurementCache.get(item);
                if (w === undefined) {
                    w = context.measureText(item).width;
                    measurementCache.set(item, w);
                }
                if (w > maxWidth) maxWidth = w;
            }
            return roundToDecimalPlaces(maxWidth, 1);
        };

        /**
         * 
         * @param {number} fontSize 
         */
        // Build font string parts once
        const fontColorStr = `#${this.fontColour.toString(16).padStart(6, '0')}`;
        const fontPrefix = (this.bold ? 'bold ' : '') + (this.italic ? 'italic ' : '');
        
        const setFontToContext = fontSize =>{            
            context.strokeStyle = fontColorStr;
            context.lineWidth = 1;
            const fontSizeScaled = fontSize * (96/72);
            context.font = `${fontPrefix}${fontSizeScaled}px ${this.fontFamily}`;
            context.textRendering = "optimizeSpeed";
            measurementCache.clear(); // Clear cache when font changes
        }

        /**
         * 
         * @param {number} width
         * @returns {string[]}
         */
        const getLines = width => {
            return text.reduce(( a, c, i ) => {
                const space = i > 0 && this.textWrapping === PlanogramLabel.textWrapping.Word? " " : "";
                var combinedText = (a[ a.length - 1 ] || "") + space + c;
                
                // Measure the combined text directly instead of using getLongestLine
                let textWidth = measurementCache.get(combinedText);
                if (textWidth === undefined) {
                    textWidth = context.measureText(combinedText).width;
                    measurementCache.set(combinedText, textWidth);
                }
                
                if (i === 0 || textWidth >= width) a.push(c);
                else  a[a.length - 1] += space + c;
                return a;
            },[]);
        }

        /**
         * 
         * @param {number} fontSize
         * @param {number} width 
         * @param {number} height 
         * @returns {number}
         */
        const shrinkText = ( fontSize, width, height ) => {
            const lineHeightMultiplier = 1.3;
            
            const getValues = (testFontSize) => {
                const lines = getLines(width);
                let longestWidth = 0;
                for (const line of lines) {
                    let w = measurementCache.get(line);
                    if (w === undefined) {
                        w = context.measureText(line).width;
                        measurementCache.set(line, w);
                    }
                    if (w > longestWidth) longestWidth = w;
                }
                return { 
                    longestWidth: roundToDecimalPlaces(longestWidth, 1), 
                    longestHeight: lines.length * (testFontSize * lineHeightMultiplier) 
                };
            }

            // Binary search for optimal font size (much faster than linear decrement)
            let minSize = 1;
            let maxSize = fontSize;
            let bestSize = minSize;
            
            while (maxSize - minSize > 0.1) {
                const midSize = (minSize + maxSize) / 2;
                setFontToContext(midSize);
                const { longestWidth, longestHeight } = getValues(midSize);
                
                if (longestWidth <= width && longestHeight <= height) {
                    bestSize = midSize;
                    minSize = midSize; // Try larger
                } else {
                    maxSize = midSize; // Try smaller
                }
            }
            
            setFontToContext(bestSize);
            return bestSize;
        }

        const lineHeightMultiplier = 1.3;
        const  [yData, zData, xData, ] = [{ maxWidth: size.z, maxHeight: size.y }, 
        { maxWidth: size.x, maxHeight: size.z }, 
        { maxWidth: size.x, maxHeight: size.y }].reduce((ac,dim, index) => {
            const directions = this.rotateToFit && this.isAttached ?  [ horizontal, vertical ] : [ this.textDirection ];
            const canvasOffset = index * (width / scaleFactor);
            
            const { fontSize, textDirection, lines, longestWidth, longestHeight } = directions.map( textDirection => {
                const { width, height } = getDimensions( textDirection, dim.maxWidth, dim.maxHeight );
                const { innerMaxWidth, innerMaxHeight } = getInnerDimensions(width,height);
                const fontSize = this.shrinkToFit || !this.isAttached? shrinkText(this.fontSize * 0.5, innerMaxWidth, innerMaxHeight) : this.fontSize * 0.5;
                const lines = getLines(innerMaxWidth);
                const longestWidth = getLongestLine(lines);
                const longestHeight = lines.length * (fontSize * lineHeightMultiplier); 
                return { fontSize, textDirection, lines, longestWidth, longestHeight };
            }).reduce((a,c)=>(a.longestWidth > c.longestWidth) ? a : c);
            
            setFontToContext(fontSize);
            context.lineWidth = 1;
            context.fillStyle = "#000000";
            
            // Pre-calculate line widths once
            const lineWidths = lines.map(line => {
                let w = measurementCache.get(line);
                if (w === undefined) {
                    w = context.measureText(line).width;
                    measurementCache.set(line, w);
                }
                return w;
            });
            
            const lineHeight = fontSize * lineHeightMultiplier;
            lines.forEach((line, i) => {
                const lineWidth = lineWidths[i];
                let offset = 0;
                if (this.textAlignment === PlanogramLabel.textAlignment.Center) {
                    offset = (longestWidth - lineWidth) / 2;
                } else if (this.textAlignment === PlanogramLabel.textAlignment.Right) {
                    offset = longestWidth - lineWidth;
                }
                context.fillText(line, offset + canvasOffset, i * lineHeight);
            });
            
            var imageData = context.getImageData(index * width, 0, width, canvas.height);
            return ac.concat([{ imageData, innerWidth: longestWidth, innerHeight: longestHeight, textDirection }]);
        },[]);
        return Promise.resolve({ x: xData, y: yData, z: zData, text: labelText, id });
    }


    /**
     * @param {THREE.Mesh} mesh 
     * @returns {Promise<PlanogramLabelImageData>}
     */
    getMeshImageData(mesh){
        if(!mesh) return Promise.resolve({ x: null, y: null, z: null, text: null, id: null });
        if( mesh.userData === null || !Reflect.has(mesh.userData,"label") || !Reflect.has(mesh.userData,"boundingBox")) return Promise.resolve(null);
        return this.getBoundingBoxImageData(mesh.userData.label, mesh.userData.boundingBox, mesh.userData.id);
    }

    /**
     * @param {PlanogramObject} planObject 
     * @returns {Promise<PlanogramLabelImageData>}
     */
    getPlanObjectImageData(planObject){
        if(planObject.labelText === null || planObject.groupBoundingBox === null) return Promise.resolve(null);
        return this.getBoundingBoxImageData(planObject.labelText, planObject.groupBoundingBox, planObject.id);
    }

    /**
     * @param {THREE.Box3} boundingBox 
     * @returns {Promise<PlanogramLabelImageData>}
     */
    getBoundingBoxImageData( text, boundingBox, id){
        if(!text) return Promise.resolve({ x: null, y: null, z: null, text: text, id: id });
        const size = boundingBox.getSize(new THREE.Vector3());
        return this.#applyFontTransforms(text, size, id);
    }

    connectedCallback(){
        this.#container = PlanogramContainer.get(this);
        this.#labelController = this.#container.labelController;
        this.updateFromAllMeshes();
        this.generateMeshes();
        this.#firstRender = false;
    }

    attributeChangedCallback(name, oldValue, newValue){
        if(this.#firstRender || oldValue === newValue) return;
        this.generateMeshes();
    }

    disconnectedCallback(){
        this.#orthographicMesh?.removeFromParent();
        this.#orthographicMesh?.dispose();
        this.#orthographicMesh = null;
        this.#perspectiveMesh?.removeFromParent();
        this.#perspectiveMesh?.dispose();
        this.#perspectiveMesh = null;
        this.#orthographicGeometry?.dispose();
        this.#orthographicGeometry = null;
        this.#perspectiveGeometry?.dispose();
        this.#perspectiveGeometry = null;
        
        if(this.#container && this.#labelController && this.#container.scene){
            const LabeledObjects = this.#container.scene.getObjectsByProperty("name", this.#name);
            LabeledObjects.forEach( obj => { this.#labelController.removeImage( obj.userData.id ); });
        }
    }

    //#region Enums
    static textWrapping = {
        None: "none",
        Letter: "letter",
        Word: "word"
    }

    static horizontalPlacement = {
        Left: "left",
        Center: "center",
        Right: "right"
    }

    static verticalPlacement = {
        Top: "top",
        Center: "center",
        Bottom: "bottom"
    }

    static textAlignment = {
        Left: "left",
        Center: "center",
        Right: "right",
        Justify: "justify"
    }

    static textDirection = {
        Horizontal: "horizontal",
        Vertical: "vertical"
    }

    static type = {
        Product: "product",
        Component: "component"
    }

    //#endregion
}

export class PlanogramLabelController{
    /**@type {THREE.DataArrayTexture} */
    #texture;
    /**@type {Map<string, PlanogramLabelImageData>} */
    #map = new Map();
    /**@type {THREE.ShaderMaterial} */
    #shader = null;
    #updateCycle = 0;
    /**@type {PlanogramContainer} */
    #container = null;

    get texture(){ return this.#texture; }

    /**
     * 
     * @param {THREE.ShaderMaterial} shader
     * @param {PlanogramContainer} container 
     */
    constructor( shader, container ){ 
        this.#shader = shader;
        this.#container = container;
    }

    /**
     * 
     * @param {string} uid 
     */
    removeImage( uid ){
        if(!this.#map.has(uid)) return;
        this.#map.delete(uid);
        this.updateData();
    }

    updateData() {
        const filteredValues = [...this.#map.values()].filter(x => x.x.imageData && x.y.imageData && x.z.imageData);
        const values = filteredValues.flatMap(labelData => [labelData.x.imageData, labelData.y.imageData, labelData.z.imageData]);
        const { width, height } = PlanogramLabelController.maxRestriction;
    
        const depth = values.length;
        
        // Don't create texture with zero depth - causes GL_INVALID_VALUE errors
        if (depth === 0) {
            if (this.#texture) {
                this.#texture.dispose();
                this.#texture = null;
                this.#shader.uniforms.image.value = null;
            }
            return;
        }
        
        const data = new Uint8Array(width * height * depth * 4);
        values.forEach((item, index) => data.set(item.data, index * width * height * 4));
        
        this.#texture?.dispose();
        this.#texture = new THREE.DataArrayTexture(data, width, height, depth);
        this.texture.name = "PlanogramLabelTexture_" + (this.#updateCycle++);
        this.#texture.format = THREE.RGBAFormat;
        this.#texture.type = THREE.UnsignedByteType;
        this.#texture.minFilter = THREE.NearestFilter;
        // this.#texture.magFilter = THREE.NearestFilter;
        this.#texture.colorSpace = THREE.SRGBColorSpace
        const renderer = PlanogramRenderer.get(this.#container);
        this.#texture.anisotropy = renderer.renderer.capabilities.getMaxAnisotropy();
        this.#shader.uniforms.image.value = this.#texture;
        // this.#shader.uniforms.imageSize.value = new THREE.Vector2( width / 4, height/ 4 );
        this.#texture.needsUpdate = true;
        this.#shader.needsUpdate = true;
        
    }

    /**
     * 
     * @param {Promise<PlanogramLabelImageData[] | PlanogramLabelImageData>[]} promises 
     */
    async updateBulk( promises ){
        if(!promises || promises.length === 0) return;
        const results = await Promise.all(promises);
        
        // Flatten results since worker now returns arrays
        const items = results.flat().filter(x => x);
        
        items.forEach( data => {
            if(!data.text){
                this.#map.delete(data.id);
            }else{
                this.#map.set(data.id, data);
            }
        });
        this.updateData();
    }

    /**
     * @param {string} uid 
     * @param {PlanogramLabelImageData} imageData 
     */
    updateImage( uid, imageData ){
        this.#map.set(uid, imageData);
        if(imageData.x && imageData.y && imageData.z){
            this.updateData();
        }else{
            console.log("something isn't going right");
        }
    }


    /**
     * 
     * @param {string} uid 
     * @returns {PlanogramLabelImageData}
     */
    getImageData(uid){
        return this.#map.get(uid);
    }

    /**
     * @param {string} uid 
     * @returns {[number,number,number]|null}
     */
    getImageDepth(uid) {
        const values = [...this.#map.keys()].filter(x=>x.data !== null);
        const index = values.findIndex(x=>x === uid) * 3; 
        return index > -1 ? [index, index + 1, index + 2]: null;
    }

    static maxRestriction = {
        width: 512,
        height: 512
    } 
}